import { Link } from "react-router-dom";
import { CLIENT_LIST, type ClientSlug } from "@/components/atrium/atriumData";
import { useTasks } from "@/hooks/useTasks";
import { useClientSnapshot } from "@/hooks/useClientSnapshot";
import { useAlerts } from "@/hooks/useAlerts";
import { useKpi } from "@/hooks/useKpi";
import { agoLabel } from "@/lib/format";

function statusFor(overdue: number, alerts: number, hasKpi: boolean) {
  if (overdue > 0 || alerts > 0) return { label: "Needs attention", color: "var(--a-bad)" };
  if (!hasKpi) return { label: "Watch", color: "var(--a-warn)" };
  return { label: "OK", color: "var(--a-ok)" };
}

function ClientRow({ slug }: { slug: ClientSlug }) {
  const meta = CLIENT_LIST.find((c) => c.slug === slug)!;
  const snap = useClientSnapshot(slug);
  const tasks = useTasks({ client: slug });
  const alerts = useAlerts();
  const kpi = useKpi();
  const open = snap.data?.open_tasks ?? 0;
  const overdue = tasks.data?.rows.filter((r) => r.bucket === "overdue").length ?? 0;
  const clientAlerts = alerts.data?.rows.filter((a) => a.client === slug).length ?? 0;
  const kpiRow = kpi.data?.find((r) => r.client === slug);
  const state = statusFor(overdue, clientAlerts, Boolean(kpiRow));

  return (
    <tr className="cursor-pointer a-row-hover" style={{ borderTop: "1px solid var(--a-border)" }}>
      <td className="px-[14px] py-3">
        <Link to={`/clients/${slug}`} className="flex items-center gap-2.5 a-link">
          <div
            className="w-[26px] h-[26px] rounded-md flex items-center justify-center text-[10px] font-bold border"
            style={{ background: "var(--a-bg)", borderColor: "var(--a-border)" }}
          >
            {meta.ticker}
          </div>
          <div>
            <div className="font-medium">{meta.name}</div>
            <div className="text-[11px]" style={{ color: "var(--a-muted)" }}>{meta.serviceScope}</div>
          </div>
        </Link>
      </td>
      <td><span className="a-chip">{meta.services.join(" / ")}</span></td>
      <td className="text-right">
        <span className="a-dot mr-1.5" style={{ background: state.color }} />
        <span className="text-[12px]" style={{ color: "var(--a-muted)" }}>{state.label}</span>
      </td>
      <td className="text-right mono">
        {kpiRow?.roas != null ? kpiRow.roas.toFixed(2) : "not connected"}
      </td>
      <td className="text-right mono">
        {kpiRow?.revenue_per_recipient != null ? `EUR ${kpiRow.revenue_per_recipient.toFixed(2)}` : "not connected"}
      </td>
      <td className="text-right mono">
        {open}{overdue ? <span style={{ color: "var(--a-bad)" }}> / {overdue}</span> : null}
      </td>
      <td className="text-right mono" style={{ color: clientAlerts ? "var(--a-bad)" : "var(--a-muted)" }}>
        {clientAlerts}
      </td>
      <td className="text-right pr-[14px] text-[12px]" style={{ color: "var(--a-muted)" }}>
        {snap.data?.last_meeting_at ? agoLabel(snap.data.last_meeting_at) : "-"}
      </td>
    </tr>
  );
}

export default function AtriumClientsList() {
  return (
    <div className="p-5 flex flex-col gap-4">
      <div className="flex items-baseline gap-3">
        <div className="text-[24px] font-semibold tracking-[-0.02em]">Clients</div>
        <div className="text-[13px]" style={{ color: "var(--a-muted)" }}>
          {CLIENT_LIST.length} active clients - live operational state only
        </div>
      </div>

      <div className="a-card overflow-hidden">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.06em]" style={{ color: "var(--a-muted)" }}>
              <th className="px-[14px] py-2.5 font-semibold">Client</th>
              <th className="font-semibold">Scope</th>
              <th className="text-right font-semibold">Status</th>
              <th className="text-right font-semibold">ROAS</th>
              <th className="text-right font-semibold">Email RPR</th>
              <th className="text-right font-semibold">Tasks</th>
              <th className="text-right font-semibold">Alerts</th>
              <th className="text-right pr-[14px] font-semibold">Last meeting</th>
            </tr>
          </thead>
          <tbody>
            {CLIENT_LIST.map((c) => <ClientRow key={c.slug} slug={c.slug} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
